import { z } from 'zod';
import {
    LIST_EVENT_APPLICATION_STATUS,
    LIST_EVENT_APPLICATION_TYPE,
    LIST_EVENT_TYPE,
    LIST_SUB_EVENT_TYPE,
    DEFAULT_EVENT_TYPE,
    DEFAULT_EVENT_APPLICATION_TYPE,
    DEFAULT_EVENT_APPLICATION_STATUS,
    DEFAULT_SUB_EVENT_TYPE, VALID_CURRENCIES
} from "@typesDef/event.types";
import {
    VALID_LANGUAGES,
    VALID_TIMEZONES,
} from "@typesDef/profile.types";
import {zBaseDocument, objectIdSchema, zDateField} from "@schemas/shared.schemas";

// Constants

const zEventApplicationType = z.enum(LIST_EVENT_APPLICATION_TYPE);
const zEventApplicationStatus = z.enum(LIST_EVENT_APPLICATION_STATUS);
const zEventType = z.enum(LIST_EVENT_TYPE);
const zSubEventType = z.enum(LIST_SUB_EVENT_TYPE);

const zValidLanguage = z.enum(VALID_LANGUAGES as [string, ...string[]]);
const zValidTimezone = z.enum(VALID_TIMEZONES as [string, ...string[]]);

const zValidCurrency = z.enum(VALID_CURRENCIES as [string, ...string[]]);

const zEventApplicationConstraints = z.object({
    languages: zValidLanguage.array().optional().default([]),
    minRank: z.number().min(0).max(5).optional().default(0),
    maxRank: z.number().min(0).max(5).optional().default(5),
    minParticipants: z.number().min(1).optional(),
    maxParticipants: z.number().min(1).optional()
});

const zEventApplication = z.object({
    type: zEventApplicationType.default(DEFAULT_EVENT_APPLICATION_TYPE),
    status: zEventApplicationStatus.default(DEFAULT_EVENT_APPLICATION_STATUS),
    // deadline: z.date().optional(),
    // closedAt: z.date().optional(),
    deadline: z.string().optional(), // in db it is saved as Date
    closedAt: z.string().optional(), // in db it is saved as Date
    terms: z.string().max(200).optional(),
    price: z.number(),
    currency: zValidCurrency.optional(),
    remarks: z.string().max(500).optional(),
    constraints: zEventApplicationConstraints
});

export const zEventSchema = zBaseDocument.extend({
    providerId: objectIdSchema,
    networkIds: z.array(objectIdSchema).max(5, "Maximum 5 networkIds allowed").optional().default([]),
    assignmentId: objectIdSchema,
    title: z.string().min(3).max(150),
    imgUri: z.string().max(200),
    actionUri: z.string().max(200).optional(),
    type: zEventType.default(DEFAULT_EVENT_TYPE),
    subType: zSubEventType.default(DEFAULT_SUB_EVENT_TYPE).optional(),
    description: z.string().min(3).max(1000),
    location: z.string().min(3).max(150),
    //startDateTime: z.date(),
    //endDateTime: z.date(),
    timezone: zValidTimezone,
    startDateTime: zDateField, // in db it is saved as Date
    endDateTime: zDateField, // in db it is saved as Date
    startEventDateTime: zDateField, // in db it is saved as Date
    endEventDateTime: zDateField, // in db it is saved as Date
    application: zEventApplication,
    isDraft: z.boolean().default(true),
    isOnboardingEvent: z.boolean().optional().default(false),
    deletedAt: z.string().optional()
});

// these are the fields which cannot be updated by the user
export const zEventProtectedFields = ['providerId', 'isOnboardingEvent', 'deletedAt'] as const;


// Inferred TypeScript type from the Zod schema
export type ZEvent = z.infer<typeof zEventSchema>;

// Zod schema for QueryResult
const zQueryResultEvents = z.object({
    count: z.number().default(0),
    results: z.array(zEventSchema).default([]),
})
export type ZQueryResultEvents = z.infer<typeof zQueryResultEvents>;

export const validateEvent = (data: object, throwError: boolean = false) => {
    if (throwError) return zEventSchema.parse(data);
    return zEventSchema.safeParse(data);
}
